/**
 * Player demographics — the "who are our players" screen.
 *
 * Unlike the owner report, nothing here names an individual: every figure is an aggregate count or a
 * summed value over a group (an age band, a municipality, a category). That is deliberate — a
 * demographic breakdown is a shape-of-the-base question, not a per-player one, so the most
 * identifying thing on this surface is "how many players are 25-34", which identifies no one.
 *
 * The source is `PROD.WHG_USERS`, the WHG account records, joined to `PROD_DATASETS.BETS` for the
 * value overlay (which age band stakes the most, etc.). The warehouse was profiled before this was
 * built: of ~3,988 users, date of birth and city are ~88% populated and reconcile to real Puerto
 * Rican municipalities, while `GENDER` is populated on 3 rows and is therefore not reported — a chart
 * that is 99.9% "unknown" is noise, not a demographic.
 *
 * City names arrive unnormalised in the source ("San Juan", "San juan", "SAN JUAN" are distinct
 * strings), so the query folds case and trims before grouping; the labels here are the folded form
 * with a presentable capitalisation applied server-side.
 */
import { z } from 'zod';
/**
 * One age band and how many players fall in it, optionally with the betting value that band drove.
 *
 * `band` is a fixed label the server assigns from date of birth (`18-24`, `25-34`, …, `65+`, and
 * `unknown` for the players whose DOB the source did not carry). `players` is the headcount; `handle`
 * and `houseResult` are that band's share of betting activity over the report window, summed in
 * dollars, so the screen can answer "the 25-34 band is the biggest group *and* stakes the most"
 * rather than only the first half.
 */
export const ageBandRowSchema = z.object({
    band: z.string(),
    players: z.number().int().nonnegative(),
    activePlayers: z.number().int().nonnegative(),
    handle: z.number(),
    houseResult: z.number(),
});
/** One municipality (or `unknown`) and its player count plus betting value over the window. */
export const municipalityRowSchema = z.object({
    municipality: z.string(),
    players: z.number().int().nonnegative(),
    activePlayers: z.number().int().nonnegative(),
    handle: z.number(),
    houseResult: z.number(),
});
/**
 * One betting channel and how many distinct players used it over the window.
 *
 * `PUNTER_CATEGORY` was the obvious segment and is not here: it is present on zero of ~5,000 player
 * rows in the warehouse, so a category chart would be a single "(none)" bar. Channel is the segment
 * that is actually populated — `BETS.CHANNEL_ID` distinguishes the mobile app, the retail shop
 * terminals (`Retail_Poc`), and the web, and it answers a real "how do our players reach us" question
 * that the owner can act on.
 */
export const channelRowSchema = z.object({
    channel: z.string(),
    players: z.number().int().nonnegative(),
    handle: z.number(),
    houseResult: z.number(),
});
/** Registrations in one calendar month, for the signup-trend line. */
export const registrationMonthSchema = z.object({
    /** `YYYY-MM`, the month the registrations fell in. */
    month: z.string().regex(/^\d{4}-\d{2}$/),
    registrations: z.number().int().nonnegative(),
});
/**
 * The whole demographics report.
 *
 * `totalPlayers` and `withDemographics` sit at the top so the screen can state its own coverage: a
 * breakdown over the 88% of players who carry a date of birth is honest only if the reader is told it
 * is 88%. The `activePlayers*` figures are scoped to the report window (players who placed at least
 * one settled bet in it); the headcounts are the whole base, because "how old is our player base" is
 * not a windowed question.
 */
export const demographicsReportSchema = z.object({
    /** The value-overlay window, `YYYY-MM-DD`. Headcounts are all-time; value sums are over this range. */
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    totalPlayers: z.number().int().nonnegative(),
    /** Players carrying a usable date of birth — the denominator the age bands actually cover. */
    withDateOfBirth: z.number().int().nonnegative(),
    /** Players carrying a usable municipality — the denominator the geography actually covers. */
    withMunicipality: z.number().int().nonnegative(),
    ageBands: z.array(ageBandRowSchema),
    municipalities: z.array(municipalityRowSchema),
    channels: z.array(channelRowSchema),
    registrations: z.array(registrationMonthSchema),
});
//# sourceMappingURL=demographics.js.map